/**
 * Shared API client configuration and error handling
 * Single responsibility: HTTP client setup and error normalization
 */

import axios from 'axios';
import type { AxiosError, AxiosInstance } from 'axios';

/** Server error envelope: { error: { code, message, details }, correlation_id } */
interface ServerErrorBody {
  error?: { code?: string; message?: string };
  /** Legacy flat shape kept as a fallback */
  message?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function handleApiError(error: unknown, fallbackMessage: string): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ServerErrorBody>;
    if (axiosError.response?.data) {
      const body = axiosError.response.data;
      throw new Error(
        body.error?.message || body.message || fallbackMessage
      );
    }
    if (axiosError.code === 'ECONNABORTED') {
      throw new Error('Request timeout - please try again');
    }
    if (!axiosError.response) {
      throw new Error(
        'Unable to connect to server - please check your connection'
      );
    }
  }
  throw new Error(fallbackMessage);
}
